import moment from 'moment'
import { normalizeSchedules, getScheduleId } from '../../../../../Instruments/utils/multiSchedule'
import { classifyScheduleEntry, SCHEDULE_SHAPE } from '../classifyScheduleEntry'
import { resolveProjectionDayCap } from '../projectionDayCap'
import {
  createProjectedOccurrence,
  OCCURRENCE_SOURCE,
  TRIGGER_KIND,
} from '../projectedOccurrence'
import { buildInstrumentDependencyOrder } from '../instrumentDependencyGraph'
import { expandEngineScheduleStudyDays } from '../engine/repeatExpansion'
import { resolveOpenForStudyDayEnd } from '../engine/availabilitySpanExpansion'
import { getRecurringOccurrenceDays } from './recurringExpansion'
import { getInstrumentChainTriggerIds, isVisitChainedSchedule } from './instrumentChainStudyDay'
import { getVisitChainedStudyDay } from './visitChainStudyDay'
import { getInstrumentChainedStudyDay } from './instrumentChainStudyDay'
import { resolveLegacyDeployStudyDay } from './resolveLegacyStudyDay'
import { resolveEngineScheduleStudyDay } from '../engine/anchorStudyDay'
import { inferEngineTriggerKind } from '../inferTriggerKind'

const inferTriggerKind = (schedule, engineSchedule) => {
  if (engineSchedule) return inferEngineTriggerKind(schedule)
  if (isVisitChainedSchedule(schedule)) return TRIGGER_KIND.visit
  if (getInstrumentChainTriggerIds(schedule).length) return TRIGGER_KIND.instrument
  if (schedule?.recurring?.scheme === 'persistent') return TRIGGER_KIND.persistent
  return TRIGGER_KIND.other
}

const applyVisibilityExtensions = (occurrenceDays, schedule, studyDay, context) => {
  const { useAbsoluteStartDate, absoluteStartDate, studyEndDay } = context
  const projectionDayCap = resolveProjectionDayCap(studyEndDay)
  let days = [...occurrenceDays]
  const relExpire = schedule?.expire?.relative_to_send_ideal
  if (relExpire && (relExpire.interval == null || relExpire.interval >= 1)) {
    const interval = Math.floor(Number(relExpire.interval)) || 1
    const visibilityDays = new Set(days)
    days.forEach(occDay => {
      for (let d = 1; d <= interval; d++) {
        const extraDay = occDay + d
        if (extraDay <= projectionDayCap) {
          visibilityDays.add(extraDay)
        }
      }
    })
    days = Array.from(visibilityDays).sort((a, b) => a - b)
  }
  if (
    schedule?.deploy?.absolute != null &&
    schedule?.expire?.absolute != null &&
    useAbsoluteStartDate &&
    absoluteStartDate
  ) {
    const startDate = moment.utc(absoluteStartDate).startOf('day')
    const expireEpoch = Number(schedule.expire.absolute)
    const expireDate = Number.isFinite(expireEpoch)
      ? moment.utc(expireEpoch * 1000).startOf('day')
      : null
    if (expireDate?.isValid() && startDate.isValid()) {
      const expireStudyDay = expireDate.diff(startDate, 'days') + 1
      if (expireStudyDay >= studyDay) {
        const visibilityDays = new Set(days)
        for (let d = studyDay; d <= expireStudyDay; d++) {
          if (d >= 1 && d <= projectionDayCap) visibilityDays.add(d)
        }
        days = Array.from(visibilityDays).sort((a, b) => a - b)
      }
    }
  }
  return days
}

export const projectInstrumentOccurrences = (instruments, context = {}) => {
  const {
    visits = [],
    selectedArmVisitIds = null,
    selectedArmId = null,
    armsForEngine = [],
    useAbsoluteStartDate = false,
    absoluteStartDate = null,
    studyEndDay = null,
    projectionMode = 'template',
    participantVisits = null,
  } = context

  const occurrences = []
  const instrumentsById = new Map(
    instruments.filter(i => i?.id != null).map(i => [String(i.id), i]),
  )
  const instrumentStudyDays = new Map()
  const resolvingInstrumentIds = new Set()

  const orderedInstruments = buildInstrumentDependencyOrder(instruments)

  const buildProjectionContext = () => ({
    useAbsoluteStartDate,
    absoluteStartDate,
    visits,
    arms: armsForEngine,
    armsForEngine,
    selectedArmId,
    selectedArmVisitIds,
    projectionMode,
    participantVisits,
    resolveInstrumentStudyDay: id => resolveInstrumentStudyDay(id),
  })

  const resolveInstrumentStudyDay = instrumentId => {
    const normalizedId = String(instrumentId)
    if (instrumentStudyDays.has(normalizedId)) return instrumentStudyDays.get(normalizedId)
    if (resolvingInstrumentIds.has(normalizedId)) return null

    const instrument = instrumentsById.get(normalizedId)
    if (!instrument) return null

    resolvingInstrumentIds.add(normalizedId)
    const projectedDays = normalizeSchedules(instrument.metadata?.schedule)
      .map(schedule =>
        resolveLegacyDeployStudyDay(instrument, schedule, {
          ...buildProjectionContext(),
          resolveInstrumentStudyDay,
        }),
      )
      .filter(day => day != null && Number.isFinite(Number(day)))
      .map(Number)

    resolvingInstrumentIds.delete(normalizedId)
    const projectedDay = projectedDays.length ? Math.min(...projectedDays) : null
    instrumentStudyDays.set(normalizedId, projectedDay)
    return projectedDay
  }

  orderedInstruments.forEach(instrument => {
    const schedules = normalizeSchedules(instrument.metadata?.schedule)
    if (!schedules.length) return

    schedules.forEach((schedule, scheduleIndex) => {
      const engineSchedule = classifyScheduleEntry(schedule) === SCHEDULE_SHAPE.v3
      let studyDay = null
      let triggerKind = inferTriggerKind(schedule, engineSchedule)

      if (engineSchedule) {
        studyDay = resolveEngineScheduleStudyDay(schedule, buildProjectionContext())
      } else if (isVisitChainedSchedule(schedule)) {
        studyDay = getVisitChainedStudyDay(schedule, visits, selectedArmVisitIds, {
          projectionMode,
          participantVisits,
          useAbsoluteStartDate,
          absoluteStartDate,
        })
        if (studyDay === null) return
      } else {
        const instrumentTriggerIds = getInstrumentChainTriggerIds(schedule)
        if (instrumentTriggerIds.length) {
          const parentStudyDays = new Map()
          instrumentTriggerIds.forEach(triggerId => {
            const parentDay = resolveInstrumentStudyDay(triggerId)
            if (parentDay != null) parentStudyDays.set(String(triggerId), parentDay)
          })
          studyDay = getInstrumentChainedStudyDay(schedule, parentStudyDays)
          if (studyDay === null) return
        } else {
          studyDay = resolveLegacyDeployStudyDay(instrument, schedule, {
            ...buildProjectionContext(),
            resolveInstrumentStudyDay,
          })
        }
      }

      if (studyDay == null) return

      let occurrenceDays = engineSchedule
        ? expandEngineScheduleStudyDays(studyDay, schedule, {
          ...buildProjectionContext(),
          studyEndDay,
        })
        : getRecurringOccurrenceDays(
          studyDay,
          schedule,
          useAbsoluteStartDate,
          absoluteStartDate,
          studyEndDay,
        )

      if (!engineSchedule && schedule?.recurring?.scheme === 'persistent') {
        triggerKind = TRIGGER_KIND.persistent
      }

      occurrenceDays = applyVisibilityExtensions(occurrenceDays, schedule, studyDay, {
        useAbsoluteStartDate,
        absoluteStartDate,
        studyEndDay,
      })

      const scheduleId = getScheduleId(schedule)
      const source = engineSchedule ? OCCURRENCE_SOURCE.engine : OCCURRENCE_SOURCE.legacy

      occurrenceDays.forEach((day, index) => {
        const studyDayEnd = engineSchedule
          ? resolveOpenForStudyDayEnd(day, schedule, studyEndDay)
          : null
        occurrences.push(
          createProjectedOccurrence({
            instrument,
            schedule,
            scheduleIndex,
            scheduleId,
            occurrenceIndex: index,
            studyDay: day,
            studyDayEnd,
            source,
            triggerKind,
          }),
        )
      })
    })
  })

  return occurrences
}
